import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BaseTableMapper } from '../../tinea-components/table/mapper/BaseTableMapper';
import { TableModel } from '../../tinea-components/table/models/input/table-model';
import { TableTh } from '../../tinea-components/table/models/input/table-th';
import { ValueCell } from '../../tinea-components/table/models/input/value-cell';
import { TableRows } from '../../tinea-components/table/models/input/table-rows';
import { TableTd } from '../../tinea-components/table/models/input/table-td';
import { ExternalApiMonitoring, ExternalApiMonitoringStatus } from 'src/swagger-gen/web-secured/models';
import { calculateDuration, extractNumber } from '@gnv-utils';

@Injectable({
  providedIn: "root",
})
export class TableProviderMonitoringList extends BaseTableMapper {
  // CSS Classes & Display Defaults
  readonly CSS_CLASS_CONFIG = "table-station-connectivity";

  // Column Identifiers
  readonly COLUMN_PROVIDER_API = "COLUMN_PROVIDER";
  readonly COLUMN_STATUS = "COLUMN_STATUS";

  readonly COLUMN_LOSS_CONNECTION_DATE = "COLUMN_LOSS_CONNECTION_DATE";
  readonly COLUMN_DISCONNECT_DURATION = "COLUMN_DISCONNECT_DURATION";
  readonly COLUMN_ERROR_MESSAGE = "COLUMN_ERROR_MESSAGE";

  constructor(private translate: TranslateService) {
    super();
    this.table = new TableModel();
  }

  /**
   * Initializes the table model with data.
   * @param monitoringRepoPagedList List of external API monitoring data.
   * @returns The configured TableModel instance.
   */
  init(monitoringRepoPagedList: ExternalApiMonitoring[]): TableModel {
    this.table = new TableModel();
    this.initConfiguration();
    this.initHeader();
    this.initDataRow(monitoringRepoPagedList);
    this.table.paginationEnabled = true;

    return this.table;
  }

  /**
   * Configures general table settings.
   */
  private initConfiguration(): void {
    this.table.configuration.cssClass = this.CSS_CLASS_CONFIG;
    this.table.configuration.noDataRowMsg =
      this.translate.instant("error.no_result");
  }

  /**
   * Initializes table headers.
   */
  private initHeader(): void {
    const headers: TableTh[] = [
      this.createHeaderCell(this.COLUMN_PROVIDER_API),
      this.createHeaderCell(this.COLUMN_STATUS),
      this.createHeaderCell(this.COLUMN_ERROR_MESSAGE),
      this.createHeaderCell(this.COLUMN_LOSS_CONNECTION_DATE),
      this.createHeaderCell(this.COLUMN_DISCONNECT_DURATION),
    ];

    this.setHeaderCells(headers);
  }

  /**
   * Populates the table with rows based on the monitoring data.
   * @param monitoringData List of ExternalApiMonitoring items.
   */
  private initDataRow(monitoringData: ExternalApiMonitoring[]): void {
    // Hide Tomorrow.io while it's not fully implemented
    const providerBlackList = ["TOMORROW_IO"];

    monitoringData
        ?.filter((providerConnectivity) => !providerBlackList.includes(providerConnectivity.externalApi!))
        ?.forEach((providerConnectivity) => {
      const tableRow = new TableRows(providerConnectivity);

      // td - provider API
      const providerCell = this.createTableCell(
        this.translateValue(
            `monitoring.table.providerApi.${providerConnectivity.externalApi}`
          ) as string,
        "STRING"
      );

      // td - provider status
      const statusValue = {
        value: this.translateValue(
          `monitoring.table.status.${providerConnectivity.status}`
        ) as string,
        cssClass: this.getStatusCssClass(providerConnectivity.status),
        tooltip: "",
      };

      const providerStatus = this.createSimpleTdTags(this.COLUMN_STATUS, [
        statusValue,
      ]);
      providerStatus.addValueCellsCss('w-6rem');

      // td - error message
      const errorMessageValue = this.getErrorMessageByHttpStatus(providerConnectivity.httpStatusCode);
      const tooltip = this.buildHttpStatusErrorTooltip(providerConnectivity.httpStatusCode, errorMessageValue);

      const errorMessageCell = this.createTableCell(
        errorMessageValue,
        "STRING",
        tooltip
      );
      // Class to truncate cell values
      errorMessageCell.addCss('flow-root-cell');
      errorMessageCell.addValueCellsCss('flow-root-cell-value');

      // td - loss connection date
      const lossConnectionDateCell = this.createTableCell(
        providerConnectivity.statusChangeDate,
        "DATE"
      );
      const lossConnectionDurationCell = this.createTableCell(
        calculateDuration(providerConnectivity.statusChangeDate as string)
      );

      tableRow.cells = [
        providerCell,
        providerStatus,
        errorMessageCell,
        lossConnectionDateCell,
        lossConnectionDurationCell,
      ];

      this.table.dataRow.push(tableRow);
    });
  }

  getErrorMessageByHttpStatus(httpStatusCode: string | null | undefined): string {
    const trimmedCode = httpStatusCode?.trim();
    const extractedNumber = trimmedCode ? extractNumber(trimmedCode) : null;

    return extractedNumber
      ? String(this.translateValue(`monitoring.table.httpStatus.${extractedNumber}`))
      : this.DISPLAY_NO_DATA;
  }


  private buildHttpStatusErrorTooltip(httpStatusCode: string | null | undefined, errorMessage: string): string {
    const trimmedCode = httpStatusCode?.trim();
    const trimmedMessage = errorMessage?.trim();

    return trimmedCode && trimmedMessage ? `${trimmedCode} : ${trimmedMessage}` : '';
  }


  /**
   * Creates a table header cell.
   * @param columnId The column identifier.
   * @returns A configured TableTh instance.
   */
  private createHeaderCell(columnId: string): TableTh {
    const header = new TableTh();
    header.columnId = columnId;

    const valueCell = new ValueCell();
    valueCell.displayedValue = this.translateValue(
      `monitoring.table.${columnId}`
    ) as string;

    header.valueCells.push(valueCell);
    return header;
  }

  /**
   * Creates a table data cell.
   * @param value The value to display.
   * @param dataType (Optional) The data type (default: STRING).
   * @returns A configured TableTd instance.
   */
  private createTableCell(
    value: string | undefined,
    dataType: "STRING" | "DATE" | "NUMBER" | "ENUM" = "STRING",
    tooltip?: string
  ): TableTd {
    const cell = new TableTd();
    const valueCell = new ValueCell();

    valueCell.displayedValue = value ?? this.DISPLAY_NO_DATA;
    valueCell.dataType = dataType;
    valueCell.tooltip = tooltip || "";

    cell.valueCells.push(valueCell);

    return cell;
  }

  private getStatusCssClass(status: ExternalApiMonitoringStatus | undefined): string {
    if (status === ExternalApiMonitoringStatus.AVAILABLE) return 'indicator-name-success';
    if (status === ExternalApiMonitoringStatus.UNAVAILABLE) return 'indicator-name-danger';
    return '';
  }

  private translateValue(value: string): string | null {
    return value ? this.translate.instant(value) : null;
  }
}
